import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import StarIcon from "@mui/icons-material/Star";
import GroupsIcon from "@mui/icons-material/Groups";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import { useAlert } from "../../../context/AlertContext";
import { getAssignablePackages } from "../../../data/packagesCatalogStore";
import { createClientPackage } from "../../../../common/utils/clientPackages";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import MemberSelectorPopup from "./MemberSelectorPopup";
import {
  PACKAGE_CATALOG,
  totalSessions,
  sessionsLabel,
  isTrialLike,
} from "../../../../common/utils/packageCatalog";
import "./packageFlow.css";

// Tablet/Desktop get a centered modal, Mobile gets a bottom sheet — same
// breakpoint convention used by every other responsive popup in this app
// (ProfilePhotoPicker, NewAppointmentFlow).
const useIsDesktopTablet = () => {
  const [isDesktopTablet, setIsDesktopTablet] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktopTablet(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isDesktopTablet;
};

const iconForEntry = (entry) => {
  if (entry.isTrial) return CardGiftcardOutlinedIcon;
  if (entry.featured) return StarIcon;
  if (entry.people > 1) return GroupsIcon;
  if (entry.sessionsPerPerson[0] === 8) return CalendarMonthOutlinedIcon;
  return FitnessCenterIcon;
};

const durationLabel = (pkg) => {
  if (!pkg) return "";
  if (Number(pkg.service_qty) === 12) return "Mesečni ciklus (1 mesec)";
  return `${pkg.period_days || 30} dana`;
};

// TEMPORARY: the remote API this dev environment points at returns 401 on
// every `client-packages/*` call (undeployed backend, not a frontend bug —
// see api/ changes from this session). This fallback lets the popup still
// be previewed/clicked through with realistic sample data instead of a
// dead-end error, clearly flagged so it's never mistaken for real data.
// Remove this block once the backend above is deployed and the real fetch
// succeeds on its own.
const PREVIEW_PACKAGES = [
  {
    id: "preview-trial",
    name: "Besplatna proba",
    description:
      "Isprobajte HitFit bez obaveza — jedan besplatan trening po vašem izboru.",
    price: 0,
    service_qty: 1,
    period_days: 7,
  },
  {
    id: "preview-single",
    name: "Pojedinačni trening",
    description: "Rezervišite jedan individualni trening kad god vam odgovara.",
    price: 2500,
    service_qty: 1,
    period_days: 30,
  },
  {
    id: "preview-eight",
    name: "8 termina",
    description: "Paket od 8 treninga po povoljnijoj ceni po terminu.",
    price: 8000,
    service_qty: 8,
    period_days: 60,
  },
  {
    id: "preview-twelve",
    name: "12 termina",
    description:
      "12 treninga mesečno uz najbolju cenu po terminu i prioritetno zakazivanje.",
    price: 10800,
    service_qty: 12,
    period_days: 30,
  },
];

const PackageAssignFlow = ({
  open,
  member,
  onClose,
  onAssigned,
  initialPackageId,
  // New Appointment's own "Dodeli paket" trigger: skip the confirm step entirely and assign the
  // moment a single-vežbač package card is picked (no start/payment date pick — defaults to
  // today, same as the confirm screen's own default), so the trainer doesn't have to click
  // through a second screen just to give a member a session to book against. Multi-vežbač
  // packages still need the confirm step regardless (picking "Član 2"/"Član 3" has nowhere else
  // to happen), and every other caller (Members) leaves this off and keeps today's exact flow.
  quickAssign = false,
}) => {
  const { showAlert } = useAlert();
  const isDesktopTablet = useIsDesktopTablet();
  // Always mounted (this component returns null when !open rather than being conditionally
  // rendered by its parent), so the lock itself must be gated on `open`, not just mount.
  useLockBodyScroll(open);

  const [step, setStep] = useState("select"); // "select" | "confirm"
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [usingPreviewData, setUsingPreviewData] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // "Dodeli i plati" opens this small popup to confirm/edit the price before assigning+marking
  // paid; "Dodeli i plati kasnije" skips it entirely (assigns unpaid, at the package's own
  // price). `payNowPrice` is a string while editing (the input's own value) — parsed to a
  // number only when actually submitting.
  const [payNowOpen, setPayNowOpen] = useState(false);
  const [payNowPrice, setPayNowPrice] = useState("");
  // Multi-vežbač packages only: the member this flow was opened for is always "Član 1" (not
  // stored here — read straight off the `member` prop), these hold the rest ("Član 2"/"Član 3"),
  // indexed 0-based (additionalMembers[0] is Član 2, [1] is Član 3). `memberPickerSlot` is which
  // of those is currently being chosen via the stacked MemberSelectorPopup, or null when closed.
  const [additionalMembers, setAdditionalMembers] = useState([]);
  const [memberPickerSlot, setMemberPickerSlot] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep("select");
    setSelectedPackage(null);
    setNotes("");
    setSaveError(null);
    setAdditionalMembers([]);
    setMemberPickerSlot(null);
    setPayNowOpen(false);
    setPayNowPrice("");

    setLoadingPackages(true);
    setUsingPreviewData(false);

    // `initialPackageId` is the "Renew" shortcut: preselect the member's current package and
    // skip straight to the confirm step instead of making the admin re-pick it. The real
    // package row only tells us its `service_qty` — map that back to the matching catalog
    // entry so `selectedPackage` has the same shape (key/people/sessionsPerPerson/...) every
    // other selection produces.
    const preselect = (list) => {
      if (initialPackageId) {
        const realPkg = list.find((p) => p.id === initialPackageId);
        if (!realPkg) return;
        const entry = isTrialLike(realPkg)
          ? PACKAGE_CATALOG.find((e) => e.isTrial)
          : PACKAGE_CATALOG.find(
              (e) => e.matchServiceQty === Number(realPkg.service_qty)
            );
        if (!entry) return;
        setSelectedPackage({
          ...entry,
          id: realPkg.id,
          period_days: realPkg.period_days,
          service_qty: totalSessions(entry),
        });
        setStep("confirm");
      }
    };

    getAssignablePackages()
      .then((res) => {
        const list = res || [];
        setPackages(list);
        preselect(list);
      })
      .catch((err) => {
        console.log(err);
        // See PREVIEW_PACKAGES above — show sample data instead of a dead
        // end so the popup can still be reviewed while the backend that
        // serves the real list isn't deployed/reachable yet.
        setPackages(PREVIEW_PACKAGES);
        setUsingPreviewData(true);
        preselect(PREVIEW_PACKAGES);
      })
      .finally(() => setLoadingPackages(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!isDesktopTablet || !open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isDesktopTablet, open, onClose]);

  // The fixed 9-entry frontend catalog is always the full list shown — never filtered by
  // whatever happens to exist in the `packages` table. Each entry is merged with a real backend
  // package when one exists for it: for a single-vežbač entry that's a childless parent package
  // matching `matchServiceQty`; for a multi-vežbač entry it's a parent package whose *children*
  // (see the Admin App's "packages with children" feature and the `children`/`TotalPackages`
  // fields on GET client-packages/packages) collectively match `sessionsPerPerson.slice(1)` —
  // `childIds` below carries each child's real id, in the same order as those extra slots, so
  // the confirm step can assign one client_package row per additional member.
  const displayPackages = useMemo(() => {
    return PACKAGE_CATALOG.map((entry) => {
      let realPkg = null;
      if (entry.isTrial) {
        realPkg = packages.find((p) => isTrialLike(p)) || null;
      } else if (entry.matchServiceQty != null) {
        const requiredChildQtys = entry.sessionsPerPerson
          .slice(1)
          .map(Number)
          .sort((a, b) => a - b);
        realPkg =
          packages.find((p) => {
            if (isTrialLike(p)) return false;
            if (Number(p.service_qty) !== entry.matchServiceQty) return false;
            const childQtys = (p.children || [])
              .map((c) => Number(c.service_qty))
              .sort((a, b) => a - b);
            return (
              childQtys.length === requiredChildQtys.length &&
              childQtys.every((qty, i) => qty === requiredChildQtys[i])
            );
          }) || null;
      }
      return {
        ...entry,
        id: realPkg ? realPkg.id : null,
        period_days: realPkg ? realPkg.period_days : null,
        service_qty: totalSessions(entry),
        childIds: realPkg ? (realPkg.children || []).map((c) => c.id) : [],
      };
    });
  }, [packages]);

  // A 2-/3-vežbač package needs one additional member picked per extra slot ("Član 1" is
  // always the `member` this flow was opened for) before it can be confirmed.
  const requiredAdditionalMembers = selectedPackage
    ? Math.max(0, (selectedPackage.people || 1) - 1)
    : 0;
  const membersComplete =
    additionalMembers.filter(Boolean).length >= requiredAdditionalMembers;

  // `selectedPackage.id` is only non-null when a real backend package was matched (see
  // `displayPackages` above) — this guards against attempting to assign a catalog entry the
  // Admin App's current package setup no longer has a matching package for.
  const canSave =
    !!selectedPackage && !!selectedPackage.id && membersComplete && !saving;

  // Selecting a package normally advances to the confirm step — no separate "select card, then
  // click Continue" step in between. One handler used by every entry point: the CTA button (all
  // platforms), a single tap on the card itself (Mobile), and a double-click on the card
  // (Desktop/Tablet, see the card's onDoubleClick below). `quickAssign` skips the confirm step
  // entirely for a single-vežbač pick — straight to `handleAssign` with today's defaults —
  // falling through to the normal confirm step for anything it can't quick-assign (a
  // multi-vežbač package still needs its member pickers; a catalog entry with no matching real
  // package still needs the confirm screen's own "not available" message).
  const handleChoosePackage = (pkg) => {
    if (saving) return;
    setSelectedPackage(pkg);
    setAdditionalMembers([]);
    setMemberPickerSlot(null);
    if (quickAssign && (pkg.people || 1) <= 1 && pkg.id != null) {
      handleAssign(pkg);
      return;
    }
    setStep("confirm");
  };

  // `pkgOverride` is `quickAssign`'s own immediate pick (see handleChoosePackage above) — state
  // updates from the same event aren't visible yet when this runs, so it can't just read
  // `selectedPackage`. The normal confirm-step buttons below call this with no `pkgOverride`,
  // using `selectedPackage`/`canSave` (unaffected) exactly as before. `options.paidNow` is the
  // "Dodeli i plati" vs "Dodeli i plati kasnije" choice (quickAssign never sets it — a quick
  // pick assigns unpaid, same as "kasnije"); `options.price` is only meaningful alongside
  // `paidNow` (the admin-confirmed price from the pay-now popup).
  const handleAssign = async (pkgOverride, options = {}) => {
    const pkg = pkgOverride || selectedPackage;
    if (!pkg || pkg.id == null || saving) return;
    if (!pkgOverride && !canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      const anchorClientId = member.id != null ? member.id : member.client_id;

      // Single-vežbač packages (always true for a `pkgOverride`/quick-assign call, since
      // handleChoosePackage only takes that path for `people <= 1`): one assignment, using the
      // parent package's own id. Multi-vežbač packages (confirm-step path only) assign the same
      // way but once per member — Član 1 (this flow's `member`) gets the parent id, each
      // additional member gets their matching child id from `childIds` (same order as
      // `additionalMembers`, both excluding the anchor) — so every member ends up with their
      // own real `clients_packages` row, sized to their own slot's service_qty.
      const assignments = pkgOverride
        ? [{ clientId: anchorClientId, packageId: pkg.id }]
        : [
            { clientId: anchorClientId, packageId: pkg.id },
            ...additionalMembers.map((person, i) => ({
              clientId: person.id != null ? person.id : person.client_id,
              packageId: pkg.childIds[i],
            })),
          ];

      // Sequential, not Promise.all: these are independent creates against the same
      // single-row `client-packages/create` endpoint (no bulk/transactional endpoint exists),
      // so if one fails partway the earlier ones have already been saved — surfacing the error
      // lets the admin see it happened and retry/inspect rather than silently losing it.
      const results = [];
      for (const { clientId, packageId } of assignments) {
        // eslint-disable-next-line no-await-in-loop
        const created = await createClientPackage({
          clientId,
          packageId,
          price: options.paidNow ? options.price : undefined,
          paymentDate: options.paidNow ? moment().format("YYYY-MM-DD") : undefined,
          notes,
        });
        results.push({ clientId, created });
      }

      showAlert(
        assignments.length > 1
          ? "Paket je uspešno dodeljen svim članovima."
          : "Paket je uspešno dodeljen.",
        "success"
      );
      if (onAssigned) onAssigned(results[0]?.created);
      onClose();
    } catch (err) {
      console.log(err);
      if (pkgOverride) {
        showAlert("Greška prilikom dodele paketa.", "error");
      } else {
        setSaveError("Greška prilikom dodele paketa. Pokušajte ponovo.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const memberName = `${member?.first_name || ""} ${member?.last_name || ""}`.trim();

  const selectContent = (
    <>
      {loadingPackages && <div className="hf-pkg-loading">Učitavanje paketa...</div>}
      {!loadingPackages && usingPreviewData && (
        <div className="hf-pkg-preview-banner">
          Prikazani su primer podaci — API sa stvarnim paketima trenutno nije
          dostupan.
        </div>
      )}
      {!loadingPackages && displayPackages.length === 0 && (
        <div className="hf-pkg-empty">
          <p>Nema dostupnih paketa.</p>
        </div>
      )}
      {!loadingPackages && displayPackages.length > 0 && (
        <div className="hf-pkg-grid">
          {displayPackages.map((pkg) => {
            const Icon = iconForEntry(pkg);
            const featured = pkg.featured;
            const selected = selectedPackage?.key === pkg.key;
            const comingSoon = pkg.id == null;
            return (
              <div
                key={pkg.key}
                role="button"
                tabIndex={0}
                className={`hf-pkg-card${featured ? " hf-pkg-card-featured" : ""}${
                  selected ? " hf-pkg-card-selected" : ""
                }${
                  pkg.people === 2
                    ? " hf-pkg-card-duo"
                    : pkg.people === 3
                    ? " hf-pkg-card-trio"
                    : ""
                }`}
                // Desktop/Tablet: a single click on the card body itself no longer does
                // anything on its own (only the CTA button below or this double-click do) —
                // Mobile has no double-tap equivalent, so a single tap there has to be enough.
                onClick={() => {
                  if (!isDesktopTablet) handleChoosePackage(pkg);
                }}
                onDoubleClick={() => {
                  if (isDesktopTablet) handleChoosePackage(pkg);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleChoosePackage(pkg);
                  }
                }}
              >
                {featured && <span className="hf-pkg-featured-badge">Preporučeno</span>}
                {selected && (
                  <span className="hf-pkg-selected-check">
                    <CheckIcon sx={{ fontSize: 11 }} />
                  </span>
                )}
                <div className="hf-pkg-card-head">
                  <span className="hf-pkg-card-icon">
                    <Icon sx={{ fontSize: 17 }} />
                  </span>
                  <span className="hf-pkg-card-name">{pkg.name}</span>
                </div>
                <div className="hf-pkg-card-meta">
                  <div className="hf-pkg-meta-row">
                    <span className="hf-pkg-meta-label">Broj vežbača</span>
                    <span
                      className={`hf-pkg-meta-value${
                        pkg.people === 2
                          ? " hf-pkg-people-badge hf-pkg-people-badge-duo"
                          : pkg.people === 3
                          ? " hf-pkg-people-badge hf-pkg-people-badge-trio"
                          : ""
                      }`}
                    >
                      {pkg.people}
                    </span>
                  </div>
                  <div className="hf-pkg-meta-row">
                    <span className="hf-pkg-meta-label">Termini</span>
                    <span className="hf-pkg-meta-value">{sessionsLabel(pkg)}</span>
                  </div>
                </div>
                {Number(pkg.price) > 0 ? (
                  <div className="hf-pkg-card-price">
                    {Number(pkg.price).toLocaleString("sr-RS")}
                    <span> RSD{featured ? " / mesečno" : ""}</span>
                  </div>
                ) : (
                  <div className="hf-pkg-card-price">Besplatno</div>
                )}
                {comingSoon && (
                  <span className="hf-pkg-card-soon">Uskoro dostupno</span>
                )}
                <button
                  type="button"
                  className={`hf-pkg-card-cta${featured ? " hf-pkg-card-cta-featured" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChoosePackage(pkg);
                  }}
                >
                  Izaberi paket
                </button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  const confirmContent = (
    <>
      <div className="hf-pkg-summary">
        {(!selectedPackage || selectedPackage.people <= 1) && (
          <div className="hf-pkg-summary-row">
            <span className="hf-pkg-summary-label">Član</span>
            <span className="hf-pkg-summary-value">{memberName || "—"}</span>
          </div>
        )}
        <div className="hf-pkg-summary-row">
          <span className="hf-pkg-summary-label">Paket</span>
          <span className="hf-pkg-summary-value">{selectedPackage?.name}</span>
        </div>
        <div className="hf-pkg-summary-row">
          <span className="hf-pkg-summary-label">Broj vežbača</span>
          <span className="hf-pkg-summary-value">{selectedPackage?.people}</span>
        </div>
        <div className="hf-pkg-summary-row">
          <span className="hf-pkg-summary-label">Termini</span>
          <span className="hf-pkg-summary-value">
            {selectedPackage ? sessionsLabel(selectedPackage) : ""}
          </span>
        </div>
        <div className="hf-pkg-summary-row">
          <span className="hf-pkg-summary-label">Cena</span>
          <span className="hf-pkg-summary-value">
            {selectedPackage
              ? Number(selectedPackage.price) > 0
                ? `${Number(selectedPackage.price).toLocaleString("sr-RS")} RSD`
                : "Besplatno"
              : ""}
          </span>
        </div>
        <div className="hf-pkg-summary-row">
          <span className="hf-pkg-summary-label">Trajanje</span>
          <span className="hf-pkg-summary-value">{durationLabel(selectedPackage)}</span>
        </div>
      </div>

      {selectedPackage && selectedPackage.people > 1 && (
        <div
          className={`hf-pkg-members${isDesktopTablet ? " hf-pkg-members-grid" : ""}`}
        >
          {Array.from({ length: selectedPackage.people }).map((_, i) => {
            const isAnchor = i === 0;
            const person = isAnchor ? null : additionalMembers[i - 1];
            const sessions = selectedPackage.sessionsPerPerson?.[i] ?? 0;
            const personName = person
              ? `${person.first_name || ""} ${person.last_name || ""}`.trim()
              : null;
            return (
              <div className="hf-pkg-member-card" key={i}>
                <div className="hf-pkg-member-card-top">
                  <span className="hf-pkg-member-card-label">Član {i + 1}</span>
                  <span className="hf-pkg-member-card-sessions">
                    {sessions} termina
                  </span>
                </div>
                {isAnchor ? (
                  <div className="hf-pkg-member-card-name">{memberName || "—"}</div>
                ) : (
                  <div className="hf-pkg-member-card-row">
                    {personName ? (
                      <span className="hf-pkg-member-card-name">{personName}</span>
                    ) : (
                      <span className="hf-pkg-member-card-placeholder">
                        Nije izabran
                      </span>
                    )}
                    <button
                      type="button"
                      className="hf-pkg-member-select-btn"
                      onClick={() => setMemberPickerSlot(i - 1)}
                    >
                      {personName ? "Promeni" : "Izaberi člana"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* All 9 catalog entries now resolve to a real Admin App package (single-vežbač parents,
          or multi-vežbač parents + children) — this only fires as a fallback if the Admin App's
          package setup no longer matches this entry (e.g. it was renamed/removed there). */}
      {selectedPackage && selectedPackage.id == null && (
        <div className="hf-pkg-error">
          Ovaj paket trenutno nije dostupan za dodelu — proverite podešavanja
          paketa u Admin aplikaciji.
        </div>
      )}

      <div className="hf-pkg-preview-status">Aktiviraće se pri prvog zakaznog treninga</div>

      <div className="hf-pkg-stat-tiles">
        <div className="hf-pkg-stat-tile">
          <div className="hf-pkg-stat-value">0</div>
          <div className="hf-pkg-stat-label">Iskorišćeno</div>
        </div>
        <div className="hf-pkg-stat-tile">
          <div className="hf-pkg-stat-value">{selectedPackage?.service_qty ?? 0}</div>
          <div className="hf-pkg-stat-label">Preostalo</div>
        </div>
        <div className="hf-pkg-stat-tile">
          <div className="hf-pkg-stat-value">0%</div>
          <div className="hf-pkg-stat-label">Napredak</div>
        </div>
      </div>

      <div className="hf-pkg-progress-track" style={{ marginBottom: 8 }}>
        <div className="hf-pkg-progress-fill" style={{ width: "0%" }} />
      </div>

      <div className="hf-pkg-field">
        <label htmlFor="pkg-notes">Notes</label>
        <textarea
          id="pkg-notes"
          className="hf-pkg-input hf-pkg-textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Npr. način plaćanja, dogovor sa članom..."
        />
      </div>

      {saveError && <div className="hf-pkg-error">{saveError}</div>}
    </>
  );

  // Kept separate from confirmContent (rather than inside it) so it can be rendered outside
  // the confirm step's scrollable middle section — pinned at the bottom alongside the pinned
  // header, instead of scrolling away with the rest of the form on a shorter window.
  const confirmFooter = (
    <div className="hf-pkg-footer">
      <button type="button" className="hf-pkg-btn-cancel" onClick={onClose}>
        Otkaži
      </button>
      <button
        type="button"
        className="hf-pkg-btn-secondary"
        disabled={!canSave}
        onClick={() => handleAssign()}
      >
        {saving && <span className="hf-btn-spinner" />}
        Dodeli i plati kasnije
      </button>
      <button
        type="button"
        className="hf-pkg-btn-primary"
        disabled={!canSave}
        onClick={() => {
          setPayNowPrice(String(selectedPackage?.price ?? 0));
          setPayNowOpen(true);
        }}
      >
        Dodeli i plati
      </button>
    </div>
  );

  // "Dodeli i plati"'s price-confirm step — a small popup stacked over the confirm screen
  // (same convention as memberPicker below), not a separate step in the main flow, since all it
  // needs is one editable number pre-filled from the package's own price.
  const payNowPopup = payNowOpen && (
    <div className="hf-pkg-overlay hf-pkg-overlay-stacked">
      <div className="hf-pkg-modal hf-pkg-modal-confirm">
        <div className="hf-pkg-header">
          <div className="hf-pkg-header-row">
            <h2 className="hf-pkg-title">Potvrdite cenu</h2>
          </div>
          <button
            type="button"
            className="hf-pkg-close"
            onClick={() => setPayNowOpen(false)}
            aria-label="Zatvori"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </button>
        </div>
        <div className="hf-pkg-field">
          <label htmlFor="pkg-pay-now-price">Cena (RSD)</label>
          <input
            id="pkg-pay-now-price"
            className="hf-pkg-input"
            type="number"
            min="0"
            value={payNowPrice}
            onChange={(e) => setPayNowPrice(e.target.value)}
            autoFocus
          />
        </div>
        <div className="hf-pkg-footer">
          <button
            type="button"
            className="hf-pkg-btn-cancel"
            onClick={() => setPayNowOpen(false)}
          >
            Otkaži
          </button>
          <button
            type="button"
            className="hf-pkg-btn-primary"
            disabled={!canSave || payNowPrice === ""}
            onClick={() => {
              setPayNowOpen(false);
              handleAssign(undefined, { paidNow: true, price: Number(payNowPrice) });
            }}
          >
            {saving && <span className="hf-btn-spinner" />}
            Potvrdi i dodeli
          </button>
        </div>
      </div>
    </div>
  );

  const body = step === "select" ? selectContent : confirmContent;
  const title = step === "select" ? "Izaberite paket" : "Potvrda paketa";
  const subtitle = memberName;

  const headerRow = (
    <div className="hf-pkg-header">
      <div className="hf-pkg-header-row">
        {step === "confirm" && (
          <button
            type="button"
            className="hf-pkg-back"
            onClick={() => setStep("select")}
            aria-label="Nazad"
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
          </button>
        )}
        <div>
          <h2 className="hf-pkg-title">{title}</h2>
          {subtitle && <p className="hf-pkg-subtitle">{subtitle}</p>}
        </div>
      </div>
      <button type="button" className="hf-pkg-close" onClick={onClose} aria-label="Zatvori">
        <CloseIcon sx={{ fontSize: 16 }} />
      </button>
    </div>
  );

  // Which additional member's picker (if any) is currently open, plus the ids it should
  // exclude — the anchor member and every OTHER already-picked slot (not this one, so
  // re-opening a filled slot still shows its current pick among the results).
  const memberPicker = (
    <MemberSelectorPopup
      open={memberPickerSlot != null}
      title={`Izaberite člana ${memberPickerSlot != null ? memberPickerSlot + 2 : ""}`}
      excludeIds={[
        member?.id,
        ...additionalMembers
          .filter((_, idx) => idx !== memberPickerSlot)
          .map((m) => m?.id),
      ]}
      onClose={() => setMemberPickerSlot(null)}
      onSelect={(client) => {
        setAdditionalMembers((prev) => {
          const next = [...prev];
          next[memberPickerSlot] = client;
          return next;
        });
        setMemberPickerSlot(null);
      }}
    />
  );

  if (isDesktopTablet) {
    return (
      <>
        <div className="hf-pkg-overlay">
          <div
            className={`hf-pkg-modal${
              step === "select" ? " hf-pkg-modal-wide" : " hf-pkg-modal-confirm"
            }`}
          >
            {headerRow}
            <div className="hf-pkg-scroll-body">{body}</div>
            {step === "confirm" && confirmFooter}
          </div>
        </div>
        {memberPicker}
        {payNowPopup}
      </>
    );
  }

  return (
    <>
      <div className="hf-pkg-overlay">
        <div className="hf-pkg-sheet hf-pkg-sheet-scroll">
          <div className="hf-pkg-sheet-handle" />
          {headerRow}
          <div className="hf-pkg-scroll-body">{body}</div>
          {step === "confirm" && confirmFooter}
        </div>
      </div>
      {memberPicker}
      {payNowPopup}
    </>
  );
};

export default PackageAssignFlow;
